import { inject, Injectable, Injector } from '@angular/core'

import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  from,
  map,
  switchMap,
  takeUntil,
  timer,
  toArray,
} from 'rxjs'

import { VehicleScheduledServiceScheduleState } from '@ti/contracts'
import { ApiService } from '@ti/core/api/services/api.service'
import { Profile } from '@ti/core/auth/services'
import { Memoize } from '@ti/core/common/decorators'
import { completeStore, injectDestroy$, toSignalState } from '@ti/core/common/utils'
import { UNIT_FORMATTING_SERVICE, UnitType } from '@ti/core/contracts/i18n'
import { LanguageService } from '@ti/core/ui-kit/i18n'
import { getWholeDaysDifference } from '@ti/core/ui-kit/i18n/utils/datetime-utils'

import { VehiclesStore } from './vehicles.store'

@Injectable({ providedIn: 'root' })
export class VehicleMaintenanceStatusProvider {
  protected readonly profile = inject(Profile)
  protected readonly vehiclesStore = inject(VehiclesStore)
  protected readonly languageService = inject(LanguageService)
  protected readonly injector = inject(Injector)
  protected readonly api = inject(ApiService)
  protected readonly unitFormattingService = inject(UNIT_FORMATTING_SERVICE)
  protected readonly destroy$ = injectDestroy$()

  protected readonly store = {
    statuses$: new BehaviorSubject<Record<string, string[]>>({}),
    refresh$: new BehaviorSubject<void>(undefined),
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      statuses: this.store.statuses$.asObservable(),
      refresh: this.store.refresh$.asObservable(),
    }
  }

  public constructor() {
    Promise.resolve()
      .then(() => this.initialize())
      .catch((e) => console.error('Cannot initialize VehicleMaintenanceStatusProvider', e))

    this.destroy$.subscribe(() => completeStore(this.store))
  }

  protected async initialize() {
    this.initStatusLoad()
  }

  protected initStatusLoad() {
    const vehicleIds$ = this.vehiclesStore.state$.items.pipe(
      map((vehicles) => vehicles.map((v) => v.id).sort()),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    )

    return combineLatest([
      vehicleIds$,
      this.profile.state,
      this.languageService.massTranslate$({
        overdue: 'common.overdue',
      }),
      this.state$.refresh,
      timer(0, 30 * 60 * 1000),
    ])
      .pipe(
        switchMap(([vehicleIds, profile, labels]) => {
          if (!vehicleIds.length) {
            return from(Promise.resolve([])).pipe(map(() => ({ services: [], profile, labels })))
          }
          return this.api.fleet.vehicleScheduledService
            .listPlanned$({
              filter: {
                scheduleState: [VehicleScheduledServiceScheduleState.Overdue],
                vehicleId: vehicleIds,
              },
            })
            .pipe(map((services) => ({ services, profile, labels })))
        }),
        switchMap(({ services, profile, labels }) =>
          from(services).pipe(
            concatMap(async (service) => {
              const schedule = service.schedule
              if (!schedule) {
                return { vehicleId: service.vehicleId, scheduleName: '' }
              }

              const timezone = profile.fleet?.timezone || 'UTC'
              const parts: string[] = []

              if (schedule.daysInterval) {
                const dayDiff = getWholeDaysDifference(new Date(), service.date, timezone)
                if (dayDiff < 0) {
                  parts.push(`${Math.abs(dayDiff)}d`)
                }
              }

              if (schedule.mileageInterval) {
                const mileageDiff = service.odometer - (service.lastOdometer || 0)
                if (mileageDiff < 0) {
                  const mileage = await this.unitFormattingService.getUnitText(
                    Math.abs(mileageDiff),
                    UnitType.Distance,
                    {
                      addLabel: true,
                      precision: 0,
                      commaSeparated: true,
                      usePreciseIfLess: null,
                    },
                  )
                  parts.push(mileage)
                }
              }

              if (schedule.engineHoursInterval) {
                const engineHoursDiff = service.engineHours - (service.lastEngineHours || 0)
                if (engineHoursDiff < 0) {
                  parts.push(`${Math.abs(engineHoursDiff)} hr`)
                }
              }

              const joinedParts = parts.join(', ')
              const scheduleName = [schedule.name, joinedParts, labels.overdue]
                .filter(Boolean)
                .join(' ')
              return { vehicleId: service.vehicleId, scheduleName }
            }),
            toArray(),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((services) => {
        const statuses: Record<string, string[]> = {}

        services.forEach((service) => {
          const { vehicleId, scheduleName } = service

          if (vehicleId && scheduleName) {
            if (!statuses[vehicleId]) {
              statuses[vehicleId] = []
            }
            statuses[vehicleId].push(scheduleName)
          }
        })

        this.store.statuses$.next(statuses)
      })
  }
}
